import { Auth } from "aws-amplify";
import Config from "../config";
import axios from "axios";

async function getToken() {
  const user = await Auth.currentAuthenticatedUser();
  const token = user.signInUserSession.idToken.jwtToken;
  return token;
}

async function getAuthHeader() {
  return { headers: { Authorization: `Bearer ${await getToken()}` } };
}

async function getPostInit(body) {
  return {
    method: "POST",
    mode: "cors", // no-cors, *cors, same-origin
    cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
    },
    body: JSON.stringify(body),
  };
}

async function getDeleteInit(body) {
  const init = {
    method: "DELETE",
    mode: "cors", // no-cors, *cors, same-origin
    cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
    },
  };
  if (body) {
    init.body = JSON.stringify(body);
  }
  return init;
}

// The backend returns validation errors as plain text; 403/404 come with an empty body.
async function responseError(response) {
  const text = await response.text();
  return new Error(text || `Request failed (${response.status})`);
}

async function getRepos() {
  const url = `${Config.FoveonicsAPI}/api/Repository/GetRepo`;
  const response = await fetch(url, await getAuthHeader());
  if (!response.ok) throw new Error("Failed to fetch repositories");
  return await response?.json();
}

async function getReposByClientId(clientId) {
  const url = `${Config.FoveonicsAPI}/api/Repository/GetReposByClientId`;
  const response = await axios.get(url, {
    params: {
      clientId: clientId,
    },
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
      "Access-Control-Allow-Origin": "*",
    },
  });
  return response.data;
}

async function getInnerNodes(nodeId, count) {
  const url = `${Config.FoveonicsAPI}/api/Repository/GetInnerNodes/${nodeId}/${count}`;
  const response = await fetch(url, await getAuthHeader());
  return await response.json();
}

async function getFileLIst(nodeId, indexTemplate) {
  const url = `${Config.FoveonicsAPI}/api/Repository/GetFileListByTemplateName/${nodeId}/${indexTemplate}`;
  const response = await fetch(url, await getAuthHeader());
  return await response.json();
}

async function updateFileName(renameFilesParams) {
  const init = await getPostInit(renameFilesParams);
  const url = `${Config.FoveonicsAPI}/api/Files/UpdateFileName`;
  return await fetch(url, init);
}

async function deleteFile(deleteFileIdsParams) {
  const init = await getDeleteInit(deleteFileIdsParams);
  const url = `${Config.FoveonicsAPI}/api/Files/DeleteFiles`;
  return await fetch(url, init);
}

async function getFileData(fileId, client) {
  const url = `${Config.FoveonicsAPI}/api/Workflow/GetFileData/${fileId}/${client}`;
  const response = await fetch(url, await getAuthHeader());
  return await response.json();
}

async function selectedFilesSendEmail(emailParams) {
  // emailParams supports legacy fields and optional bundle fields:
  // deliveryMode: "multipleLinks" | "singleBundleLink"
  // bundleLinkName: string
  const init = await getPostInit(emailParams);
  const url = `${Config.FoveonicsAPI}/api/Email/SelectedFilesSendEmail`;
  return await fetch(url, init);
}

async function fillCliftonExpungmentForm(body) {
  const init = await getPostInit(body);
  const url = `${Config.FoveonicsAPI}/api/Workflow/FillCliftonExpungmentForm`;
  return await fetch(url, init);
}

async function fillCliftonJuvenileExpungmentForm(body) {
  const init = await getPostInit(body);
  const url = `${Config.FoveonicsAPI}/api/Workflow/FillCliftonJuvenileExpungmentForm`;
  return await fetch(url, init);
}

async function fillCliftonJuvenileFileReportsExpungmentForm(body) {
  const init = await getPostInit(body);
  const url = `${Config.FoveonicsAPI}/api/Workflow/FillCliftonJuvenileFileReportsExpungmentForm`;
  return await fetch(url, init);
}

async function fillGlenrockArrestExpungmentForm(body) {
  const init = await getPostInit(body);
  const url = `${Config.FoveonicsAPI}/api/Workflow/FillGlenRockArrestExpungmentForm`;
  return await fetch(url, init);
}

async function fillAberdeenArrestExpungmentForm(body) {
  const init = await getPostInit(body);
  const url = `${Config.FoveonicsAPI}/api/Workflow/FillAberdeenExpungmentForm`;
  return await fetch(url, init);
}

async function fillMedfordExpungmentForm(body) {
  const init = await getPostInit(body);
  const url = `${Config.FoveonicsAPI}/api/Workflow/FillMedfordExpungmentForm`;
  return await fetch(url, init);
}

async function fillEnglewoodExpungmentForm(body) {
  const init = await getPostInit(body);
  const url = `${Config.FoveonicsAPI}/api/Workflow/FillEnglewoodExpungmentForm`;
  return await fetch(url, init);
}

async function fillDemoExpungmentForm(body) {
  const init = await getPostInit(body);
  const url = `${Config.FoveonicsAPI}/api/Workflow/FillDemoExpungmentForm`;
  return await fetch(url, init);
}

async function fillEastamptonArrestReportExpungmentForm(body) {
  const init = await getPostInit(body);
  const url = `${Config.FoveonicsAPI}/api/Workflow/FillEastamptonArrestReportExpungmentForm`;
  return await fetch(url, init);
}
async function fillLyndhurstExpungmentForm(body) {
  const init = await getPostInit(body);
  const url = `${Config.FoveonicsAPI}/api/Workflow/FillLyndhurstExpungmentForm`;
  return await fetch(url, init);
}
async function fillWayneExpungmentForm(body) {
  const init = await getPostInit(body);
  const url = `${Config.FoveonicsAPI}/api/Workflow/FillWayneExpungmentForm`;
  return await fetch(url, init);
}
async function getTemplates() {
  const url = `${Config.FoveonicsAPI}/api/Templates/`;
  const response = await fetch(url, await getAuthHeader());
  return await response.json();
}

async function getTemplate(tableName) {
  const url = `${Config.FoveonicsAPI}/api/Templates/${tableName}/`;
  const response = await fetch(url, await getAuthHeader());
  return await response.json();
}

async function searchTemplate(selectedTemplate, searchQueryString) {
  const url = `${Config.FoveonicsAPI}/api/Templates/${selectedTemplate}/search?${searchQueryString}`;
  const response = await fetch(url, await getAuthHeader());
  return await response.json();
}
async function chatSearchDocuments(request) {
  const url = `${Config.FoveonicsAPI}/api/ChatSearch/search`;
  const response = await axios.post(url, request, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
      "Access-Control-Allow-Origin": "*",
    },
  });
  return response.data;
}

async function searchByBlockAndLot(request) {
  const url = `${Config.FoveonicsAPI}/api/ChatSearch/block-lot-search`;
  const response = await axios.post(url, request, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await getToken()}`,
      'Access-Control-Allow-Origin': '*',
    },
  });
  return response.data;
}

async function aiNaturalLanguageSearch(request, cancelToken) {
  const url = `${Config.FoveonicsAPI}/api/ChatSearch/ai-search`;
  const response = await axios.post(url, request, {
    cancelToken,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
      "Access-Control-Allow-Origin": "*",
    },
  });
  return response.data;
}

function createAiSearchCancellation() {
  return axios.CancelToken.source();
}

async function ragRetrieval(request, cancelToken) {
  const response = await axios.post(`${Config.FoveonicsAPI}/api/ChatSearch/rag-retrieval`, request, {
    cancelToken,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${await getToken()}` },
  });
  return response.data;
}

async function ragAnswer(request, cancelToken) {
  const response = await axios.post(`${Config.FoveonicsAPI}/api/ChatSearch/rag-answer`, request, {
    cancelToken,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${await getToken()}` },
  });
  return response.data;
}

async function generateReport(request, cancelToken) {
  // The server-owned SearchSession is the entire scope. Never forward row selections.
  const body = { searchSessionId: request.searchSessionId, reportRequest: request.reportRequest };
  const response = await axios.post(`${Config.FoveonicsAPI}/api/ChatSearch/report`, body, {
    cancelToken,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${await getToken()}` },
  });
  return response.data;
}

async function documentSummaries(request, cancelToken) {
  const body = { searchSessionId: request.searchSessionId, scope: request.scope,
    documents: (request.documents || []).map(d => ({ templateId: d.templateId, fileId: d.fileId })),
    includeCombinedSummary: request.includeCombinedSummary === true };
  const response = await axios.post(`${Config.FoveonicsAPI}/api/ChatSearch/summaries`, body, {
    cancelToken, headers: { "Content-Type": "application/json", Authorization: `Bearer ${await getToken()}` },
  });
  return response.data;
}

async function submitAiSearchFeedback(request) {
  const url = `${Config.FoveonicsAPI}/api/ChatSearch/ai-feedback`;
  const response = await axios.post(url, request, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
      "Access-Control-Allow-Origin": "*",
    },
  });
  return response.data;
}

// Wrapper to reuse existing repository fetch logic for ChatSearch.
// This ensures repository permission filtering is reused (RepositoryController).
async function getRepositoriesForChatSearch() {
  return await getRepositories();
}

// ---- DocumentSync AI entitlements (EntitlementsController) ---------------------------------------------------

// What the signed-in user may use: { features: { indexSearch, textSearch, semanticSearch, generativeAi },
// tenant, user, usage, lockReason, showLockedTabs, clientName }. The Search screen reads it once per visit.
async function getEntitlements() {
  const url = `${Config.FoveonicsAPI}/api/Entitlements/me`;
  const response = await fetch(url, await getAuthHeader());
  if (!response.ok) throw await responseError(response);
  return await response.json();
}

// Administration (DocumentSync_ManageUsers for seats, FoveonicsAdmin for subscriptions).
async function getAiSeats(clientId, includeRevoked = false) {
  const params = new URLSearchParams();
  if (clientId) params.set("clientId", clientId);
  if (includeRevoked) params.set("includeRevoked", "true");
  const query = params.toString();
  const url = `${Config.FoveonicsAPI}/api/Entitlements/seats${query ? `?${query}` : ""}`;
  const response = await fetch(url, await getAuthHeader());
  if (!response.ok) throw await responseError(response);
  return await response.json();
}

async function assignAiSeat({ userId, email }, clientId) {
  const query = clientId ? `?clientId=${encodeURIComponent(clientId)}` : "";
  const url = `${Config.FoveonicsAPI}/api/Entitlements/seats${query}`;
  const response = await fetch(url, await getPostInit({ userId: userId || null, email: email || null }));
  if (!response.ok) throw await responseError(response);
  return await response.json();
}

async function revokeAiSeat(userId, clientId) {
  const query = clientId ? `?clientId=${encodeURIComponent(clientId)}` : "";
  const url = `${Config.FoveonicsAPI}/api/Entitlements/seats/${encodeURIComponent(userId)}${query}`;
  const response = await fetch(url, await getDeleteInit());
  if (!response.ok) throw await responseError(response);
  return await response.json();
}

async function getAiSubscription(clientId) {
  const url = `${Config.FoveonicsAPI}/api/Entitlements/subscription/${encodeURIComponent(clientId)}`;
  const response = await fetch(url, await getAuthHeader());
  if (response.status === 404) return null;
  if (!response.ok) throw await responseError(response);
  return await response.json();
}

async function putAiSubscription(clientId, subscription) {
  const url = `${Config.FoveonicsAPI}/api/Entitlements/subscription/${encodeURIComponent(clientId)}`;
  const init = await getPostInit(subscription);
  init.method = "PUT";
  const response = await fetch(url, init);
  if (!response.ok) throw await responseError(response);
  return await response.json();
}

async function getTemplates2() {
  const url = `${Config.FoveonicsAPI}/api/ManageIndexTemplate/GetTemplates/`;
  const response = await fetch(url, await getAuthHeader());
  return await response.json();
}

async function getRepositoriesByClient(clientId) {
  const url = `${Config.FoveonicsAPI}/api/Repository/GetRepositoriesByClient/${clientId}`;
  const response = await fetch(url, await getAuthHeader());
  return await response.json();
}

async function getAllClients(repositoryId) {
  const url = `${Config.FoveonicsAPI}/api/Repository/GetAllClients/`;
  const response = await fetch(url, await getAuthHeader());
  return await response.json();
}

async function getTemplatesBasedOnRepositoryId(repositoryId) {
  const url = `${Config.FoveonicsAPI}/api/Templates/GetTemplatedBasedOnRepository/${repositoryId}`;
  const response = await fetch(url, await getAuthHeader());
  return await response.json();
}

async function getRepositories() {
  const url = `${Config.FoveonicsAPI}/api/Repository/GetRepositories/`;
  const response = await fetch(url, await getAuthHeader());
  return await response.json();
}

async function deleteTemplate(tableName) {
  const init = await getDeleteInit();
  const url = `${Config.FoveonicsAPI}/api/ManageIndexTemplate/DeleteTemplate/${tableName}`;
  return await fetch(url, init);
}

async function getAllTemplateColumns(tableName) {
  const url = `${Config.FoveonicsAPI}/api/ManageIndexTemplate/GetAllTemplatesColumns/${tableName}`;
  const response = await fetch(url, await getAuthHeader());
  return await response.json();
}

async function addIndexTemplateData(addNewTemplateParams) {
  const init = await getPostInit(addNewTemplateParams);
  const url = `${Config.FoveonicsAPI}/api/ManageIndexTemplate/AddIndexTemplateData/`;
  const response = await fetch(url, init);
  return await response.json();
}

async function updateFieldData(rowData) {
  const init = await getPostInit(rowData);
  const url = `${Config.FoveonicsAPI}/api/ManageIndexTemplate/UpdateFieldData`;
  return await fetch(url, init);
}

async function deleteColTemplate(deleteParams) {
  const init = await getDeleteInit(deleteParams);
  const url = `${Config.FoveonicsAPI}/api/ManageIndexTemplate/DeleteColTemplate`;
  return await fetch(url, init);
}

async function addFieldData(addParams) {
  const init = await getPostInit(addParams);
  const url = `${Config.FoveonicsAPI}/api/ManageIndexTemplate/AddFieldData/`;
  return await fetch(url, init);
}

async function signUp(userDetails) {
  const init = await getPostInit(userDetails);
  const url = `${Config.FoveonicsAPI}/api/ManageUsers/SignUp`;
  return await fetch(url, init);
}

async function listGroups() {
  const url = `${Config.FoveonicsAPI}/api/ManageUsers/ListGroups`;
  const response = await fetch(url, await getAuthHeader());
  return await response.json();
}

async function updateGroups(userDetails) {
  const init = await getPostInit(userDetails);
  const url = `${Config.FoveonicsAPI}/api/ManageUsers/UpdateGroups`;
  return await fetch(url, init);
}

async function getListValue(tableName) {
  const url = `${Config.FoveonicsAPI}/api/ManageIndexTemplate/GeListValue/${tableName}`;
  const response = await fetch(url, await getAuthHeader());
  return await response.json();
}

async function getListValueField(tableName, fieldName) {
  const url = `${Config.FoveonicsAPI}/api/ManageIndexTemplate/GetListValuesField/${tableName}/${fieldName}`;
  const response = await fetch(url, await getAuthHeader());
  return await response.json();
}

async function updateControlListData(rowData) {
  const init = await getPostInit(rowData);
  const url = `${Config.FoveonicsAPI}/api/ManageIndexTemplate/UpdateControlListData`;
  return await fetch(url, init);
}

async function deleteListField(controlledListId) {
  const init = await getDeleteInit();
  const url = `${Config.FoveonicsAPI}/api/ManageIndexTemplate/DeleteListField/${controlledListId}`;
  return await fetch(url, init);
}

async function addFieldListData(addParams) {
  const init = await getPostInit(addParams);
  const url = `${Config.FoveonicsAPI}/api/ManageIndexTemplate/AddFieldListData/`;
  const response = await fetch(url, init);
  return await response.json();
}

async function addRepoData(newRow) {
  const init = await getPostInit(newRow);
  const url = `${Config.FoveonicsAPI}/api/Repository/AddRepoData/`;
  const response = await fetch(url, init);
  if (!response.ok) throw await responseError(response);
  return await response.json();
}

async function saveNewClient(clientName) {
  const init = await getPostInit(clientName);
  const url = `${Config.FoveonicsAPI}/api/Repository/AddClientData/`;
  const response = await fetch(url, init);
  return await response.json();
}

async function deleteRepository(repositoryId) {
  const init = await getDeleteInit();
  const url = `${Config.FoveonicsAPI}/api/Repository/DeleteRepository/${repositoryId}`;
  return await fetch(url, init);
}

async function updateRepoData(rowData) {
  const init = await getPostInit(rowData);
  const url = `${Config.FoveonicsAPI}/api/Repository/UpdateRepoData/`;
  const response = await fetch(url, init);
  if (!response.ok) throw await responseError(response);
  return response;
}

async function getDepartments(clientId) {
  const url = `${Config.FoveonicsAPI}/api/Department?clientId=${clientId}`;
  const response = await fetch(url, await getAuthHeader());
  if (!response.ok) throw await responseError(response);
  return await response.json();
}

async function createDepartment(clientId, departmentName) {
  const init = await getPostInit({ clientId, departmentName });
  const url = `${Config.FoveonicsAPI}/api/Department`;
  const response = await fetch(url, init);
  if (!response.ok) throw await responseError(response);
  return await response.json();
}

async function renameDepartment(departmentId, departmentName) {
  const init = { ...(await getPostInit({ departmentName })), method: "PUT" };
  const url = `${Config.FoveonicsAPI}/api/Department/${departmentId}`;
  const response = await fetch(url, init);
  if (!response.ok) throw await responseError(response);
}

async function deleteDepartment(departmentId) {
  const init = await getDeleteInit();
  const url = `${Config.FoveonicsAPI}/api/Department/${departmentId}`;
  const response = await fetch(url, init);
  if (!response.ok) throw await responseError(response);
}

async function addFolder(jArrayData) {
  const init = await getPostInit(jArrayData);
  const url = `${Config.FoveonicsAPI}/api/Repository/AddFolder/`;
  const response = await fetch(url, init);
  return await response.text();
}

async function deleteFolder(nodeId) {
  const init = await getDeleteInit();
  const url = `${Config.FoveonicsAPI}/api/Repository/DeleteFolder/${nodeId}`;
  return await fetch(url, init);
}

async function moveOrCopyFiles(moveFileParameter) {
  const url = `${Config.FoveonicsAPI}/api/Files/MoveOrCopyFile`;
  const response = await axios.post(url, moveFileParameter, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
      "Access-Control-Allow-Origin": "*",
    },
  });
  return response;
}

async function getPageCount(fileId) {
  const url = `${Config.FoveonicsAPI}/api/Files/GetPageCount`;
  const response = await axios.post(url, fileId, {
    params: {
      fileId: fileId,
    },
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
      "Access-Control-Allow-Origin": "*",
    },
  });
  return response;
}

async function updateUserRoles(userId, roleIds, clientId, userEmail, ipAccess) {
  const url = `${Config.FoveonicsAPI}/api/ManageUsers/UpdateUserRoles`;

  // Prepare the request payload
  const userDetails = {
    userId: userId,
    roleIds: roleIds, // This should be an array of role IDs (strings)
    clientId: clientId,
    userEmail: userEmail,
    ipAccess: ipAccess,
  };

  // Make the POST request with the payload, headers, and token
  const response = await axios.post(url, userDetails, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`, // Assuming you have an async getToken function
      "Access-Control-Allow-Origin": "*", // If needed for CORS
    },
  });

  return response; // Return the response to handle further if needed
}

async function getListRoles(clientId, email) {
  const url = `${Config.FoveonicsAPI}/api/ManageUsers/GetListRoles`;
  const response = await axios.get(url, {
    params: {
      clientId: clientId,
      email: email,
    },
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
      "Access-Control-Allow-Origin": "*",
    },
  });
  return response.data;
}

async function getClientDetails(allClientRequired = false) {
  // Define the URL for the API call
  const url = `${Config.FoveonicsAPI}/api/ManageUsers/GetClientDetails`;

  try {
    // Make the API request using axios
    const response = await axios.get(url, {
      params: {
        allClientRequired: allClientRequired,
      },
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${await getToken()}`, // Use the token for authorization
        "Access-Control-Allow-Origin": "*", // Allow cross-origin
      },
    });

    // Return the API response
    return response.data;
  } catch (error) {
    console.error("Error fetching user details:", error);
    throw error; // Handle or propagate the error as needed
  }
}

async function updateClientMfaRequirement(clientId, requireMfa) {
  // Define the URL for the API call
  const url = `${Config.FoveonicsAPI}/api/ManageUsers/UpdateMfaRequirement`;

  try {
    // Make the API request using axios
    const response = await axios.post(
      url,
      {
        clientId: clientId,
        requireMfa: requireMfa,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await getToken()}`, // Use the token for authorization
          "Access-Control-Allow-Origin": "*", // Allow cross-origin
        },
      },
    );

    // Return the API response
    return response.data;
  } catch (error) {
    console.error("Error updating MFA requirement:", error);
    throw error; // Handle or propagate the error as needed
  }
}

async function getRequireMfabyClientSql(clientId) {
  // Define the URL for the API call
  const url = `${Config.FoveonicsAPI}/api/Authentication/GetRequireMfaByClientId/${clientId}`;

  try {
    // Make the API request using axios
    const response = await axios.get(url, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${await getToken()}`, // Use the token for authorization
        "Access-Control-Allow-Origin": "*", // Allow cross-origin
      },
    });

    // Return the API response
    return response.data.requireMfa;
  } catch (error) {
    console.error("Error fetching MFA requirement:", error);
    throw error; // Handle or propagate the error as needed
  }
}

async function getUserDetailsByEmail(email) {
  // Define the URL for the API call
  const url = `${Config.FoveonicsAPI}/api/Authentication/GetUserDetailsByEmail`;

  try {
    // Make the API request using axios
    const response = await axios.get(url, {
      params: {
        email: email, // Pass the email parameter to the API call
      },
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${await getToken()}`, // Use the token for authorization
        "Access-Control-Allow-Origin": "*", // Allow cross-origin
      },
    });

    // Return the API response
    return response.data;
  } catch (error) {
    console.error("Error fetching user details:", error);
    throw error; // Handle or propagate the error as needed
  }
}

async function getUsersByClientId(clientId) {
  // Define the URL for the API call
  const url = `${Config.FoveonicsAPI}/api/ManageUsers/GetUsersWithRolesByClientId`;
  try {
    // Make the API request using axios
    const response = await axios.get(url, {
      params: {
        clientId: clientId, // Pass the email parameter to the API call
      },
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${await getToken()}`, // Use the token for authorization
        "Access-Control-Allow-Origin": "*", // Allow cross-origin
      },
    });

    // Return the API response
    return response.data;
  } catch (error) {
    console.error("Error fetching user details:", error);
    throw error; // Handle or propagate the error as needed
  }
}

async function startOcr(templateId) {
  const url = `${Config.FoveonicsAPI}/api/Ocr/StartOcr`;
  const response = await axios.post(url, templateId, {
    params: {
      templateId: templateId,
    },
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
      "Access-Control-Allow-Origin": "*",
    },
  });
  return response;
}

async function getInitializedOcrStatus(templateId) {
  const url = `${Config.FoveonicsAPI}/api/Ocr/GetInitializedOcrStatus`;
  const response = await axios.get(url, {
    params: {
      templateId: templateId,
    },
    headers: {
      Authorization: `Bearer ${await getToken()}`,
      "Access-Control-Allow-Origin": "*",
    },
  });
  return response;
}

async function UpdateAutoOcrItem(templateId, autoOcr) {
  const url = `${Config.FoveonicsAPI}/api/Ocr/UpdateAutoOcrItem`;
  const response = await axios.put(
    url,
    {
      TemplateId: templateId,
      AutoOcr: autoOcr,
    },
    {
      headers: {
        Authorization: `Bearer ${await getToken()}`,
        "Access-Control-Allow-Origin": "*",
      },
    },
  );
  return response;
}

async function getOcrTemplatesInfo() {
  const url = `${Config.FoveonicsAPI}/api/Ocr/GetOcrStatusWithTemplatesAndFileCount`;
  const response = await axios.get(url, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
      "Access-Control-Allow-Origin": "*",
    },
  });
  return await response;
}

async function insertOrUpdateUserProfile(data) {
  const url = `${Config.FoveonicsAPI}/api/Files/InsertOrUpdateUserProfile`;
  const response = await axios.post(url, data, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
      "Access-Control-Allow-Origin": "*",
    },
  });
  return response;
}

async function getUserProfile(data) {
  const url = `${Config.FoveonicsAPI}/api/Templates/GetUserProfile`;
  const response = await axios.post(url, data, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
      "Access-Control-Allow-Origin": "*",
    },
  });
  return response;
}

async function getUserProfileWithOutTemplate(data) {
  const url = `${Config.FoveonicsAPI}/api/Templates/GetUserProfileWithOutTemplate`;
  const response = await axios.post(url, data, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
      "Access-Control-Allow-Origin": "*",
    },
  });
  return response;
}

async function listUsersWithoutGroups() {
  const url = `${Config.FoveonicsAPI}/api/ManageUsers/ListUsersWithoutGroups`;
  const response = await fetch(url, await getAuthHeader());
  return await response.json();
}

async function listGroupsForUser2(username) {
  const init = await getPostInit({ username });
  const url = `${Config.FoveonicsAPI}/api/ManageUsers/ListGroupsForUser2`;
  const response = await fetch(url, init);
  return await response.json();
}

async function listReposForUser(userEmail) {
  const init = await getPostInit({ userEmail });
  const url = `${Config.FoveonicsAPI}/api/ManageUsers/ListReposForUser`;
  const response = await fetch(url, init);
  return await response.json();
}

async function updateRepoAccess(username, repos, userEmail) {
  const init = await getPostInit({ username, repos, userEmail });
  const url = `${Config.FoveonicsAPI}/api/ManageUsers/UpdateRepoAccess`;
  return await fetch(url, init);
}

async function templateHasOcredFiles(templateId) {
  const url = `${Config.FoveonicsAPI}/api/OCR/TemplateHasOcredFiles/${templateId}`;
  const response = await fetch(url, await getAuthHeader());
  return await response.text();
}

async function uploadFile(addFileParams) {
  const url = `${Config.FoveonicsAPI}/api/Files/AddAndUploadFiles`;
  const init = await getPostInit(addFileParams);
  const response = await fetch(url, init);
  return await response.text();
}

async function deleteUser(deleteUserParams) {
  const init = await getDeleteInit(deleteUserParams);
  const url = `${Config.FoveonicsAPI}/api/ManageUsers/DeleteUsers`;
  return await fetch(url, init);
}

async function getGeoLoaction() {
  var config = {
    crossDomain: true,
  };
  const url = Config.Geolocation;
  const response = await axios.get(url, config, {
    headers: {
      "Access-Control-Allow-Origin": "*",
    },
  });
  //console.log(response);
  return response.data.ip;
}

async function checkIpAddress(ipParams) {
  const url = `${Config.FoveonicsAPI}/api/Authentication/CheckIpAddress`;
  const init = await getPostInit(ipParams);
  const response = await fetch(url, init);
  return response;
}

async function saveAuditTrail(auditDetails) {
  const url = `${Config.FoveonicsAPI}/api/Authentication/SaveAuditTrail`;
  const init = await getPostInit(auditDetails);
  return await fetch(url, init);
}

async function getSavedRestrictedIps(userEmailAddress) {
  const url = `${Config.FoveonicsAPI}/api/ManageUsers/GetSavedRestrictedIps/${userEmailAddress}`;
  const response = await fetch(url, await getAuthHeader());
  return await response.text();
}

async function uploadFileChunk(addFileParams) {
  const url = `${Config.FoveonicsAPI}/api/Files/UploadChunks`;
  const response = await axios.post(url, addFileParams.chunk, {
    params: {
      chunkFileName: addFileParams.chunkFileName,
      count: addFileParams.count,
    },
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
      "Access-Control-Allow-Origin": "*",
    },
  });
  return response;
}

async function updateUserProfileByEmail(userDetails) {
  const url = `${Config.FoveonicsAPI}/api/Authentication/UpdateUserProfileByEmail`;
  const response = await axios.put(url, userDetails, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
      "Access-Control-Allow-Origin": "*",
    },
  });
  return response;
}

async function getUserProfileByEmail(email) {
  const url = `${Config.FoveonicsAPI}/api/Authentication/GetUserProfileByEmail`;
  const response = await axios.get(url, {
    params: { email },
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
      "Access-Control-Allow-Origin": "*",
    },
  });
  return response;
}

async function uploadFileComplete(addFileParams) {
  const url = `${Config.FoveonicsAPI}/api/Files/UploadComplete`;
  const response = await axios.post(
    url,
    {},
    {
      params: {
        chunkFileName: addFileParams.chunkFileName,
        tableName: addFileParams.tableName,
        nodeId: addFileParams.nodeId,
        originalFileName: addFileParams.originalFileName,
        appendPrependOperation: addFileParams.appendPrependOperation,
        appendPrependFileId: addFileParams.appendPrependFileId,
        insertPageAfter: addFileParams.insertPageAfter,
        email: addFileParams.email,
      },
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${await getToken()}`,
        "Access-Control-Allow-Origin": "*",
      },
    },
  );
  return response;
}

async function listRedactionReasons(clientId) {
  const url = `${
    Config.FoveonicsAPI
  }/api/RedactionReasons/List/${encodeURIComponent(clientId)}`;
  const token = await getToken();
  const { data } = await axios.get(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data; // an array of strings
}

/**
 * Add a new reason.
 */
export async function addRedactionReason(clientId, reason) {
  const url = `${Config.FoveonicsAPI}/api/RedactionReasons/Add`;
  const token = await getToken();
  await axios.post(
    url,
    { clientId, reason },
    { headers: { Authorization: `Bearer ${token}` } },
  );
}

/**
 * Delete an existing reason.
 */
export async function deleteRedactionReason(clientId, reason) {
  const url = `${Config.FoveonicsAPI}/api/RedactionReasons/Delete`;
  const token = await getToken();
  await axios.delete(url, {
    headers: { Authorization: `Bearer ${token}` },
    data: { clientId, reason },
  });
}

export async function reorderRedactionReasons(clientId, reasons) {
  const url = `${Config.FoveonicsAPI}/api/RedactionReasons/Reorder`;
  const token = await getToken();

  await axios.post(
    url,
    { clientId, reasons },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );
}

async function getArchivalData(clientId) {
  const url = `${Config.FoveonicsAPI}/api/Archive/GetReposByClientId`;
  const response = await axios.get(url, {
    params: {
      clientId: clientId,
    },
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
      "Access-Control-Allow-Origin": "*",
    },
  });
  return response.data;
}

async function getAllRepoArchivalData() {
  const url = `${Config.FoveonicsAPI}/api/Archive/GetAllRepos`;
  const response = await axios.get(url, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
      "Access-Control-Allow-Origin": "*",
    },
  });
  return response.data;
}

async function archiveFolder(rowData, clientId, to, cc) {
  console.log(rowData);
  const url = `${Config.FoveonicsAPI}/api/Archive/copy-folder`;
  var finalClientId = clientId > 0 ? clientId : rowData.clientId;
  let request = {
    Prefix: rowData.s3Path,
    RepositoryId: rowData.repositoryId,
    clientId: finalClientId,
    toEmail: to,
    ccEmail: cc,
  };
  const response = await axios.post(url, request, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
      "Access-Control-Allow-Origin": "*",
    },
  });
  return response.data;
}

export async function AddUserAndRolesForAzure(email, rawAzureGroups, clientId) {
  const url = `${Config.FoveonicsAPI}/api/AzureManageUsers/AddUserAndRolesForAzure`;
  const payload = { email, rawAzureGroups, clientId };

  const token = await getToken();
  const response = await axios.post(url, payload, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
}
export async function getDeletedUsersByClientId(clientId) {
  const url = `${Config.FoveonicsAPI}/api/ManageUsers/deleted`;
  try {
    const response = await axios.get(url, {
      params: { clientId },
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${await getToken()}`,
      },
    });
    return response.data; // [{ userId, email }, …]
  } catch (error) {
    console.error("Error fetching deleted users:", error);
    throw error;
  }
}

export async function restoreUsers(users) {
  const url = `${Config.FoveonicsAPI}/api/ManageUsers/restore`;
  await axios.post(url, users, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
    },
  });
}

export async function updateArchiveInfo(repositoryId, contact, email) {
  const url = `${Config.FoveonicsAPI}/api/Archive/UpdateClientInfo`;
  // adjust URL & HTTP method (PUT vs POST) to your back-end contract
  await axios.post(
    url,
    {
      repositoryId,
      clientContactforArchives: contact,
      clientContactforArchivesEmail: email,
    },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${await getToken()}`,
      },
    },
  );
}

export async function getClientDetailsArchive(allClientRequired = false) {
  // Define the URL for the API call
  const url = `${Config.FoveonicsAPI}/api/Archive/GetClientDetails`;

  try {
    // Make the API request using axios
    const response = await axios.get(url, {
      params: {
        allClientRequired: allClientRequired,
      },
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${await getToken()}`, // Use the token for authorization
        "Access-Control-Allow-Origin": "*", // Allow cross-origin
      },
    });

    // Return the API response
    return response.data;
  } catch (error) {
    console.error("Error fetching user details:", error);
    throw error; // Handle or propagate the error as needed
  }
}

export async function getDsRoles() {
  const url = `${Config.FoveonicsAPI}/api/ManageAzure/DsRoles`;
  const response = await axios.get(url, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
      "Access-Control-Allow-Origin": "*",
    },
  });
  return response.data; // [{id,name,roleDescription}]
}

// GET /api/ManageAzure/RolePresets
export async function getRolePresets() {
  const url = `${Config.FoveonicsAPI}/api/ManageAzure/RolePresets`;
  const response = await axios.get(url, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
      "Access-Control-Allow-Origin": "*",
    },
  });
  return response.data; // { readOnly: number[], superUser: number[] }
}

// PUT /api/ManageAzure/RolePresets
export async function putRolePresets(payload) {
  // payload = { readOnly: number[], superUser: number[] }
  const url = `${Config.FoveonicsAPI}/api/ManageAzure/RolePresets`;
  const response = await axios.put(url, payload, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
      "Access-Control-Allow-Origin": "*",
    },
  });
  return response.data; // NoContent -> undefined
}

// GET /api/ManageAzure/Mappings?clientId=xxx
export async function getAzureMappingsByClient(clientId) {
  const url = `${Config.FoveonicsAPI}/api/ManageAzure/Mappings`;
  const response = await axios.get(url, {
    params: { clientId },
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
      "Access-Control-Allow-Origin": "*",
    },
  });
  return response.data; // array of mappings
}

// POST /api/ManageAzure/Mappings
export async function createAzureMapping(payload) {
  // payload = {
  //   dsClientId, azureIdp, tenantId, azureClientId, azureClientSecret,
  //   aadGroupId_SuperUser, aadGroupId_ReadOnly
  // }
  const url = `${Config.FoveonicsAPI}/api/ManageAzure/Mappings`;
  const response = await axios.post(url, payload, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
      "Access-Control-Allow-Origin": "*",
    },
  });
  return response.data; // 201 -> may be empty
}

// 1) Does user exist in Membership?
export async function userExists(email) {
  const API = `${Config.FoveonicsAPI}/api/AzureManageUsers`;
  const url = `${API}/User/Exists?email=${encodeURIComponent(email)}`;
  try {
    console.log("[userExists]", url);
    const response = await axios.get(url, {
      headers: {
        "Access-Control-Allow-Origin": "*",
      },
    });
    return response.data; // { exists: boolean }
  } catch (err) {
    console.error("[userExists][ERROR]", err?.response || err);
    throw err;
  }
}

// 2) Get ClientId for this email (Membership -> Users join)
export async function getUserClientByEmail(email) {
  const API = `${Config.FoveonicsAPI}/api/AzureManageUsers`;
  const url = `${API}/User/Client?email=${encodeURIComponent(email)}`;
  try {
    console.log("[getUserClientByEmail]", url);
    const response = await axios.get(url, {
      headers: {
        "Access-Control-Allow-Origin": "*",
      },
    });
    return response.data; // { clientId }
  } catch (err) {
    console.error("[getUserClientByEmail][ERROR]", err?.response || err);
    throw err;
  }
}

// 3) Get IdP alias for a client
export async function getClientIdp(clientId) {
  const API = `${Config.FoveonicsAPI}/api/AzureManageUsers`;
  const url = `${API}/Client/Idp/${clientId}`;
  try {
    console.log("[getClientIdp]", url);
    const response = await axios.get(url, {
      headers: {
        "Access-Control-Allow-Origin": "*",
      },
    });
    return response.data; // { idpAlias }
  } catch (err) {
    console.error("[getClientIdp][ERROR]", err?.response || err);
    throw err;
  }
}

export async function fillSomervilleExpungmentForm(body) {
  // console.log("fillSomervilleExpungmentForm", body);

  const init = await getPostInit(body); // { headers, body, ... }
  const url = `${Config.FoveonicsAPI}/api/Workflow/FillSomervilleExpungementForm`;

  const payload =
    init && typeof init.body === "string"
      ? JSON.parse(init.body)
      : (init?.body ?? body);

  const res = await axios.post(url, payload, { headers: init?.headers });

  // IMPORTANT: return the response so caller can read res.status
  return res; // or: return { status: res.status, data: res.data };
}

export async function fillRaritanExpungementForm(body) {
  // console.log("fillRaritanExpungementForm", body);

  const init = await getPostInit(body); // { headers, body, ... }
  const url = `${Config.FoveonicsAPI}/api/Workflow/FillRaritanExpungementForm`;

  const payload =
    init && typeof init.body === "string"
      ? JSON.parse(init.body)
      : (init?.body ?? body);

  const res = await axios.post(url, payload, { headers: init?.headers });

  // IMPORTANT: return the response so caller can read res.status
  return res; // or: return { status: res.status, data: res.data };
}

export async function getRedactionColor(email) {
  const url = `${Config.FoveonicsAPI}/api/Authentication/GetRedactionColorByEmail`;
  console.log("getRedactionColor URL:", url, email);
  const response = await axios.get(url, {
    params: { email },
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
      "Access-Control-Allow-Origin": "*",
    },
  });
  return response;
}

async function saveRecordManagementEntry(payload) {
  console.log("saveRecordManagementEntry payload:", payload);
  // TODO: Replace with actual backend endpoint when API is ready.
  const url = `${Config.FoveonicsAPI}/api/record-management/save`;
  return await axios.post(url, payload, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
      "Access-Control-Allow-Origin": "*",
    },
  });
}

async function deleteRecordManagementEntry(payload) {
  console.log("deleteRecordManagementEntry payload:", payload);
  // TODO: Replace with actual backend endpoint when API is ready.
  const url = `${Config.FoveonicsAPI}/api/record-management/delete`;
  return await axios.post(url, payload, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
      "Access-Control-Allow-Origin": "*",
    },
  });
}

async function updateRecordManagementEntry(payload) {
  console.log("updateRecordManagementEntry payload:", payload);
  // TODO: Replace with actual backend endpoint when API is ready.
  const url = `${Config.FoveonicsAPI}/api/record-management/update`;
  return await axios.post(url, payload, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
      "Access-Control-Allow-Origin": "*",
    },
  });
}

async function getRecordManagementEntries(clientId) {
  // TODO: Replace with actual backend endpoint when API is ready.
  const url = `${Config.FoveonicsAPI}/api/record-management/${clientId}`;
  return await axios.get(url, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getToken()}`,
      "Access-Control-Allow-Origin": "*",
    },
  });
}

export {
  getRepos,
  getRepositories,
  getInnerNodes,
  updateFileName,
  getFileData,
  selectedFilesSendEmail,
  fillCliftonExpungmentForm,
  fillLyndhurstExpungmentForm,
  fillWayneExpungmentForm,
  getTemplates,
  getTemplate,
  searchTemplate,
  getRepositoriesForChatSearch,
  getEntitlements,
  getAiSeats,
  assignAiSeat,
  revokeAiSeat,
  getAiSubscription,
  putAiSubscription,
  chatSearchDocuments,
  searchByBlockAndLot,
  aiNaturalLanguageSearch,
  ragRetrieval,
  ragAnswer,
  generateReport,
  documentSummaries,
  createAiSearchCancellation,
  submitAiSearchFeedback,
  getTemplates2,
  getAllTemplateColumns,
  getRepositoriesByClient,
  getAllClients,
  addIndexTemplateData,
  addFieldData,
  updateFieldData,
  deleteColTemplate,
  deleteTemplate,
  signUp,
  listGroupsForUser2,
  listReposForUser,
  listUsersWithoutGroups,
  listGroups,
  updateGroups,
  getListValue,
  getListValueField,
  updateControlListData,
  deleteListField,
  addFieldListData,
  addRepoData,
  saveNewClient,
  deleteRepository,
  updateRepoData,
  getDepartments,
  createDepartment,
  renameDepartment,
  deleteDepartment,
  updateRepoAccess,
  templateHasOcredFiles,
  fillMedfordExpungmentForm,
  addFolder,
  deleteFolder,
  deleteFile,
  uploadFile,
  getTemplatesBasedOnRepositoryId,
  getFileLIst,
  getGeoLoaction,
  checkIpAddress,
  getSavedRestrictedIps,
  saveAuditTrail,
  moveOrCopyFiles,
  uploadFileChunk,
  uploadFileComplete,
  deleteUser,
  getPageCount,
  insertOrUpdateUserProfile,
  getUserProfile,
  getUserProfileWithOutTemplate,
  fillEnglewoodExpungmentForm,
  getOcrTemplatesInfo,
  startOcr,
  getInitializedOcrStatus,
  fillCliftonJuvenileExpungmentForm,
  fillCliftonJuvenileFileReportsExpungmentForm,
  fillGlenrockArrestExpungmentForm,
  fillAberdeenArrestExpungmentForm,
  fillDemoExpungmentForm,
  fillEastamptonArrestReportExpungmentForm,
  UpdateAutoOcrItem,
  getUserDetailsByEmail,
  getUsersByClientId,
  getClientDetails,
  getListRoles,
  updateUserRoles,
  getReposByClientId,
  updateClientMfaRequirement,
  getRequireMfabyClientSql,
  getArchivalData,
  archiveFolder,
  getUserProfileByEmail,
  updateUserProfileByEmail,
  getAllRepoArchivalData,
  listRedactionReasons,
  saveRecordManagementEntry,
  deleteRecordManagementEntry,
  updateRecordManagementEntry,
  getRecordManagementEntries,
};
